import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { enrollTotp, verifyEnrollment, listFactors, unenrollFactor } from "@/lib/mfa";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  blocking?: boolean;
  onCompleted?: () => void;
};

export default function MfaEnrollDialog({ open, onOpenChange, blocking, onCompleted }: Props) {
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) {
      setQr(null);
      setSecret(null);
      setFactorId(null);
      setCode("");
      setError(null);
      setDone(false);
      return;
    }
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Clean up any stale unverified factor so enrollment is idempotent.
        const { all } = await listFactors();
        for (const f of all) {
          if (f.status !== "verified") {
            try { await unenrollFactor(f.id); } catch { /* ignore */ }
          }
        }
        const data = await enrollTotp();
        setFactorId(data.id);
        setQr(data.totp?.qr_code ?? null);
        setSecret(data.totp?.secret ?? null);
      } catch (err: any) {
        setError(err?.message ?? "Could not start enrollment.");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factorId) return;
    setError(null);
    setLoading(true);
    try {
      await verifyEnrollment(factorId, code);
      setDone(true);
      onCompleted?.();
    } catch (err: any) {
      setError(err?.message ?? "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!blocking) onOpenChange(v); }}>
      <DialogContent className="max-w-md" onInteractOutside={(e) => blocking && e.preventDefault()} onEscapeKeyDown={(e) => blocking && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{done ? "Two-factor enabled" : "Enable two-factor authentication"}</DialogTitle>
          <DialogDescription>
            {done
              ? "You'll be asked for a code from your authenticator app on future sign-ins."
              : "Scan the QR with Google Authenticator, 1Password, Authy, or any TOTP app, then enter the 6-digit code."}
          </DialogDescription>
        </DialogHeader>

        {!done && (
          <form onSubmit={onVerify} className="space-y-4">
            {qr ? (
              <div className="flex justify-center">
                <img src={qr} alt="MFA QR code" className="w-48 h-48 bg-white p-2 rounded" />
              </div>
            ) : (
              <div className="text-sm text-muted-foreground text-center py-8">Preparing…</div>
            )}

            {secret && (
              <div className="text-xs text-muted-foreground text-center break-all">
                Can't scan? Enter this key manually: <code className="font-mono">{secret}</code>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="enroll-code">6-digit code</Label>
              <Input
                id="enroll-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={8}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
              />
            </div>

            {error && (
              <div role="alert" className="text-destructive text-sm bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={loading || code.length < 6 || !factorId}>
              {loading ? "Verifying…" : "Verify and enable"}
            </Button>
            {!blocking && (
              <Button type="button" variant="ghost" className="w-full" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            )}
          </form>
        )}

        {done && (
          <Button className="w-full" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
